import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@llura/ui';

import { signIn, signUp } from '../../lib/auth-client';
import { GOOGLE_OAUTH_ENABLED } from '../../lib/config';
import { ApiError } from '../../lib/api-client';
import { useStrings } from '../../hooks/useStrings';
import { useCoachStore } from '../../stores/coach.store';
import { useCovenantStore } from '../../stores/covenant.store';
import { useRitualStore } from '../../stores/ritual.store';
import { useIntroStore } from '../../stores/intro.store';
import { useI18nStore } from '../../stores/i18n.store';
import { usePersonalityStore } from '../../stores/personality.store';
import { snapshotForClaim } from '../../lib/claim-snapshot';

export function SignupPage() {
  const navigate = useNavigate();
  const { strings } = useStrings();
  const t = strings.auth.signup;
  const oauth = strings.auth.oauth;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const coach = useCoachStore();
  const covenant = useCovenantStore();
  const ritual = useRitualStore();
  const intro = useIntroStore();
  const i18n = useI18nStore();
  const personality = usePersonalityStore();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t.errorPasswordTooShort);
      return;
    }
    setBusy(true);
    try {
      // Email signup requires verification, so there's no session yet to POST
      // /me/claim. Carry the pre-auth coach/covenant/ritual draft in on the
      // signUp body instead — the API applies it server-side in its create hook
      // (see auth.service.ts), so the config lands in the DB even when the
      // verify link is opened in a different browser. Idempotent: boot-sync's
      // claimFirst() re-runs harmlessly for the same-browser/OAuth paths.
      const claim = snapshotForClaim({ coach, covenant, ritual, intro, i18n, personality });
      const hasClaim = Object.keys(claim).length > 0;
      const { error: signUpError } = await signUp.email({
        email,
        password,
        name,
        // Land the verify-link tab on the welcome screen after Better Auth
        // auto-signs the user in. Origin is a trusted origin, so it passes
        // callback validation.
        callbackURL: `${window.location.origin}/welcome`,
        ...(hasClaim ? { pendingClaim: JSON.stringify(claim) } : {}),
      });
      if (signUpError) {
        // NOTE: with `requireEmailVerification: true` (see auth.service.ts),
        // better-auth deliberately does NOT error on a duplicate email — it
        // returns a fake success (anti-enumeration) and sends the user to
        // /verify-email. So this branch is currently unreachable for duplicates;
        // it's kept as defense-in-depth in case that config ever changes. The
        // duplicate-user case is handled by the "Sign in instead" path on the
        // verify-email screen instead.
        if (signUpError.status === 422 || /already/i.test(signUpError.message ?? '')) {
          setError(t.errorEmailTaken);
        } else {
          setError(signUpError.message || t.errorGeneric);
        }
        setBusy(false);
        return;
      }

      navigate('/verify-email', { state: { email } });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.errorGeneric;
      setError(message);
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-[-15%] h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-10%] left-[-10%] h-[380px] w-[380px] rounded-full bg-secondary/10 blur-3xl"
      />

      <header className="sticky top-0 z-10 flex items-center gap-2 bg-background/80 px-4 py-3 backdrop-blur">
        <span className="font-serif text-lg">{strings.brand.name}</span>
      </header>

      <main className="relative mx-auto w-full max-w-md px-5 pb-16 pt-8">
        <h1 className="font-serif text-3xl">{t.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t.intro}</p>

        {GOOGLE_OAUTH_ENABLED && (
          <>
            <Button
              variant="secondary"
              className="mt-8 w-full"
              onClick={() =>
                void signIn.social({ provider: 'google', callbackURL: `${window.location.origin}/` })
              }
            >
              {oauth.googleCta}
            </Button>
            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {oauth.orSeparator}
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}

        <form onSubmit={submit} className={GOOGLE_OAUTH_ENABLED ? 'space-y-4' : 'mt-8 space-y-4'}>
          <Field
            label={t.nameLabel}
            type="text"
            value={name}
            onChange={setName}
            placeholder={t.namePlaceholder}
            autoComplete="name"
            required
          />
          <Field
            label={t.emailLabel}
            type="email"
            value={email}
            onChange={setEmail}
            placeholder={t.emailPlaceholder}
            autoComplete="email"
            required
          />
          <Field
            label={t.passwordLabel}
            type="password"
            value={password}
            onChange={setPassword}
            hint={t.passwordHint}
            autoComplete="new-password"
            required
            minLength={8}
          />

          {error && (
            <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? t.submitting : t.submitCta}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t.loginPrompt}{' '}
          <Link to="/login" className="text-primary underline-offset-4 hover:underline">
            {t.loginLink}
          </Link>
        </p>
      </main>
    </div>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  ...rest
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      <input
        {...rest}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}
